// La modifica di una persona del team: nome, mansione, istruzioni, strumenti, modello e
// ragionamento. Gli strumenti si scelgono dal catalogo (spazio di lavoro, rete, connessioni).
import { useState } from 'react';
import { ApiError, api } from '../../lib/api';
import type {
  AgentProfile,
  AgentProfileInput,
  ConnectedProvider,
  DefaultChoice,
  ToolGroup,
} from '../../lib/api';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Field, TextArea, TextField } from '../../components/Field';
import { Select } from '../../components/Select';
import { ModelPicker } from './ModelPicker';
import { Pop } from '../../components/Pop';
import { PopPicker } from './PopPicker';
import { POP_ROLES, popRole, withRole } from '../../lib/pops';

export type Grant = { value: string; label: string; also: string[] };

// le voci concedibili: ogni strumento dello spazio, la rete intera, ogni connessione (che vale
// anche se il profilo la cita per tipo, es. «memo»)
export function grants(groups: ToolGroup[]): Grant[] {
  return groups.flatMap((g) => {
    if (g.kind === 'workspace')
      return g.tools.map((t) => ({ value: t.name, label: t.name, also: [] }));
    if (g.kind === 'web')
      return [{ value: 'web', label: 'Rete', also: g.tools.map((t) => t.name) }];
    return [{ value: g.id, label: g.name, also: g.kind && g.kind !== g.id ? [g.kind] : [] }];
  });
}

const CLI_NAMES: Record<string, string> = {
  'claude-code': 'Claude Code',
  codex: 'Codex',
  antigravity: 'Antigravity',
};

// `claude-code:opus` → «Claude Code · opus»; un modello del locale resta com'è
export function modelLabel(model: string): string {
  const [kind, name] = model.split(':', 2);
  return name && CLI_NAMES[kind] ? `${CLI_NAMES[kind]} · ${name}` : model;
}

// l'id di un pop nuovo nasce dal nome: minuscolo, senza accenti, parole unite da _; se è già
// preso gli si aggiunge un numero. Il server accetta ^[a-z][a-z0-9_]{1,31}$.
export function idFromName(name: string, taken: string[]): string {
  const base =
    name
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^[^a-z]+|_+$/g, '')
      .slice(0, 28) || 'pop';
  const stem = base.length < 2 ? `${base}_pop` : base;
  let id = stem;
  for (let n = 2; taken.includes(id); n++) id = `${stem}_${n}`;
  return id;
}

// mansione e istruzioni sono un testo solo: per un pop di prima si mettono insieme, la mansione
// in testa se le istruzioni non cominciano già così
function asOneText(a: AgentProfile): { duty: string; instructions: string } {
  const duty = a.duty.trim();
  const text = a.instructions.trim();
  return { duty: '', instructions: duty && !text.startsWith(duty) ? `${duty}\n\n${text}` : text };
}

const BLANK: AgentProfileInput = {
  id: '',
  label: '',
  team: '',
  duty: '',
  instructions: '',
  tools: ['list_files', 'read_file'],
  model: null,
  thinking: true,
  effort: null,
  avatar: null,
  nickname: '',
};

export function AgentEditor({
  agent,
  groups,
  providers,
  defaultModel,
  defaultChoice,
  takenIds,
  onSaved,
  onRemove,
  onClose,
}: {
  agent: AgentProfile | null;
  groups: ToolGroup[];
  providers: ConnectedProvider[];
  defaultModel: string | null;
  defaultChoice: DefaultChoice | null;
  takenIds: string[];
  onSaved: (a: AgentProfile) => void;
  onRemove: (a: AgentProfile) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState<AgentProfileInput>(
    agent ? { ...agent, effort: agent.effort ?? null, ...asOneText(agent) } : BLANK,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (patch: Partial<AgentProfileInput>) => setForm((f) => ({ ...f, ...patch }));
  const has = (g: Grant) => [g.value, ...g.also].some((x) => form.tools.includes(x));
  const flip = (g: Grant) =>
    set({
      tools: has(g)
        ? form.tools.filter((t) => t !== g.value && !g.also.includes(t))
        : [...form.tools, g.value],
    });

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const label = form.label.trim();
      const id = agent ? agent.id : idFromName(label, takenIds);
      // un solo testo: il server ne prende la prima riga come mansione per il PM
      onSaved(await api.saveProfile({ ...form, id, label, duty: '' }));
    } catch (e) {
      setError(e instanceof ApiError && e.detail ? e.detail : 'Non riesco a salvare.');
      setBusy(false);
    }
  };

  return (
    <aside
      aria-label={agent ? `Modifica ${agent.label}` : 'Nuovo pop'}
      style={{
        background: 'var(--panel)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-xl)',
        boxShadow: 'var(--shadow-1)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          padding: '16px 20px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        {form.avatar && <Pop avatar={form.avatar} size={56} />}
        <h2 className="t-h2" style={{ margin: 0, flex: 1 }}>
          {agent ? agent.label : 'Nuovo pop'}
        </h2>
        <Button variant="icon" icon="x" aria-label="Chiudi" onClick={onClose} />
      </div>
      <div style={{ padding: '16px 20px', display: 'grid', gap: 14 }}>
        {error && <Banner kind="danger">{error}</Banner>}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 12,
          }}
        >
          <Field label="Ruolo del pop">
            <TextField value={form.label} onChange={(v) => set({ label: v })} autoFocus={!agent} />
          </Field>
          <Field
            label="Nome del pop"
            hint="Il suo nome proprio, es. Dario. Vuoto: quello di partenza."
          >
            <TextField value={form.nickname} onChange={(v) => set({ nickname: v })} />
          </Field>
          <Field label="Ruolo">
            <Select
              label="Ruolo"
              value={form.avatar ? popRole(form.avatar) : ''}
              onChange={(r) => set({ avatar: withRole(form.avatar, r) })}
              options={[
                ...(form.avatar ? [] : [{ value: '', label: 'Scegli un ruolo' }]),
                ...POP_ROLES.map((r) => ({ value: r.id, label: r.label })),
              ]}
              width="100%"
            />
          </Field>
          <Field label="Team">
            <TextField value={form.team} onChange={(v) => set({ team: v })} />
          </Field>
        </div>
        <Field
          label="Avatar"
          hint={form.avatar ? undefined : 'Scegli un ruolo: PopTown ne propone uno anche da solo.'}
        >
          <PopPicker
            role={form.avatar ? popRole(form.avatar) : null}
            value={form.avatar}
            onChange={(avatar) => set({ avatar })}
          />
        </Field>
        <Field
          label="Mansione · system message del pop"
          hint="La prima riga la legge il PM per decidere chi fa cosa; tutto il testo è il system message del pop."
        >
          <TextArea value={form.instructions} onChange={(v) => set({ instructions: v })} rows={8} />
        </Field>
        <ModelPicker
          providers={providers}
          value={{ model: form.model, effort: form.effort, thinking: form.thinking }}
          onChange={(v) => set(v)}
          follows={defaultChoice ? { choice: defaultChoice, resolved: defaultModel } : undefined}
        />
        <div>
          <div className="t-caption dim" style={{ marginBottom: 6 }}>
            Strumenti
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {grants(groups).map((g) => {
              const on = has(g);
              return (
                <button
                  key={g.value}
                  type="button"
                  aria-pressed={on}
                  onClick={() => flip(g)}
                  className="t-mono-xs"
                  style={{
                    padding: '4px 10px',
                    minHeight: 28,
                    borderRadius: 'var(--r-full)',
                    cursor: 'pointer',
                    border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
                    background: on ? 'var(--accent-soft)' : 'transparent',
                    color: on ? 'var(--accent)' : 'var(--dim)',
                  }}
                >
                  {g.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <div
        style={{
          padding: '12px 20px',
          borderTop: '1px solid var(--border)',
          display: 'flex',
          gap: 10,
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
      >
        {agent ? (
          <Button
            icon="trash"
            disabled={agent.coordinator}
            title={
              agent.coordinator ? 'Il coordinatore non si toglie: scompone i piani.' : undefined
            }
            onClick={() => onRemove(agent)}
          >
            Togli
          </Button>
        ) : (
          <span />
        )}
        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!form.label.trim()}
            onClick={() => void save()}
          >
            Salva
          </Button>
        </div>
      </div>
    </aside>
  );
}
